import React, { useState, useMemo, forwardRef } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { EmtTypography, EmtIcon, EmtPaper } from 'emt-components-v2'
import LinkButton from '@/components/EMT/LinkButton'
import { Routes } from '@/assets/configs'
import { useIsResponsive } from '@/hooks'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import { useQueryClient } from '@tanstack/react-query'
import { usePetsittersQueries } from '@/hooks/usePetsittersQueries'
import { useRouter } from 'next/router'

import { PetsitterAvatar } from './PetsitterAvatar'
import { EmptyPetsitterSlot } from './EmptyPetsitterSlot'

import { useNewRequestCartStore } from '@/store/newRequestCartStore'

interface PetsittersSelectionProps {
  sx?: SxProps<Theme>
  marginLeftTitle?: string
  reactNativeWebviewClassName?: boolean
  openFloatingProfile?: ReturnType<typeof useFloatingProfile>['handleOpenProfile']
}

const PetsittersSelection = forwardRef<HTMLDivElement, PetsittersSelectionProps>(
  ({ sx, marginLeftTitle, reactNativeWebviewClassName = true, openFloatingProfile }, ref) => {
    const [isEditing, setIsEditing] = useState(false)
    const [removingPetsitterId, setRemovingPetsitterId] = useState<string | null>(null)
    const [isMobile] = useIsResponsive()
    const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
    const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)
    const queryClient = useQueryClient()
    const router = useRouter()

    const isSearchRoute =
      router.pathname === Routes.PetSitter.Search || router.asPath.startsWith(`${Routes.PetSitter.Search}?`)

    const continueHref = isSearchRoute
      ? `${Routes.PetSitter.Reservations.NewRequest}?from-search=true`
      : Routes.PetSitter.Reservations.NewRequest

    const petsitterQueries = usePetsittersQueries(petsittersIds)

    const petsitters = useMemo(
      () => petsitterQueries.filter((query) => query.isSuccess).map((query) => query.data),
      [petsitterQueries],
    )

    const isLoadingPetsitters = useMemo(() => petsitterQueries.some((query) => query.isLoading), [petsitterQueries])

    const handleModifyClick = () => {
      setIsEditing((prev) => !prev)
    }

    const handleRemovePetsitter = async (uuid: string) => {
      setRemovingPetsitterId(uuid)
      try {
        await removePetsitter(uuid)
        queryClient.invalidateQueries(['cartPetsitters'])
      } finally {
        setRemovingPetsitterId(null)
        if (isEditing && petsittersIds.length <= 1) {
          setIsEditing(false)
        }
      }
    }

    return (
      <Box
        ref={ref}
        sx={{
          position: isMobile ? 'sticky' : 'fixed',
          bottom: isMobile ? 'unset' : 0,
          top: isMobile ? '16px' : 'unset',
          left: 0,
          zIndex: 1000,
          width: '100%',
          display: isMobile && petsittersIds.length === 0 ? 'none' : 'block',
          ...sx,
        }}
        className={reactNativeWebviewClassName ? 'EmtReactNativeWebViewTop' : ''}
      >
        <EmtPaper
          sx={{
            padding: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: '0',
            borderRadius: isMobile ? '16px' : '16px 16px 0 0',
            boxShadow: '0px -4px 56px 0px #00000014,  0px -12px 32px 0px #00000014',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
          }}
        >
          {/* Left */}
          <Box sx={{ flexShrink: 2, maxWidth: '410px', width: '100%', minWidth: '255px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                flexShrink: 0,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <EmtTypography
                  variant="body1"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    lineHeight: '1.1',
                    marginLeft: isMobile && marginLeftTitle ? marginLeftTitle : 0,
                  }}
                >
                  Votre sélection
                </EmtTypography>
                <EmtTypography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#A5A3A9', lineHeight: '1.1', flexShrink: 0 }}
                >
                  {petsittersIds.length} / 10
                </EmtTypography>
              </Box>
              {petsittersIds.length > 0 && (
                <EmtTypography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#AE59C5',
                    lineHeight: '1.1',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={handleModifyClick}
                >
                  {isEditing ? 'Terminer' : 'Modifier'}
                </EmtTypography>
              )}
            </Box>
            {!isMobile && (
              <EmtTypography variant="body2" sx={{ color: '#4A4754', marginTop: '8px' }}>
                Sélectionnez jusqu&apos;à 10 pet sitters pour maximiser les chances de recevoir des réponses positives à
                votre demande.
              </EmtTypography>
            )}
          </Box>
          {/* END Left */}

          {/* Center */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 1,
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                padding: '0 2px',
                overflowX: isMobile ? 'scroll' : 'unset',
                maxWidth: '100%',
                paddingRight: isMobile ? '57px' : '0',
              }}
            >
              {petsitters?.map((petsitter) => (
                <PetsitterAvatar
                  key={petsitter.uuid}
                  uuid={petsitter.uuid}
                  name={petsitter.name}
                  mainPicture={petsitter.mainPicture}
                  isEditing={isEditing}
                  isLoading={removingPetsitterId === petsitter.uuid}
                  onRemove={() => handleRemovePetsitter(petsitter.uuid)}
                  onOpenProfile={() => {
                    openFloatingProfile && openFloatingProfile(petsitter.uuid, ProfileKind.PetSitting)
                  }}
                />
              ))}
              {Array.from({ length: 10 - (petsitters?.length || 0) }).map((_, index) => (
                <EmptyPetsitterSlot
                  key={`empty-${index}`}
                  isLoading={isLoadingPetsitters && index < petsittersIds.length - (petsitters?.length || 0)}
                />
              ))}
            </Box>
            {isMobile && (
              <Box
                sx={{
                  background: 'linear-gradient(270deg, #FFFFFF 20.49%, rgba(250, 250, 250, 0) 107.87%)',
                  position: 'absolute',
                  zIndex: 99,
                  right: 0,
                  top: 0,
                  height: '100%',
                  width: '57px',
                }}
              />
            )}
          </Box>

          {/* END Center */}

          {/* Right */}
          <Box
            sx={{
              flexShrink: 0,
              width: isMobile ? '100%' : '347px',
              maxWidth: isMobile ? '100%' : '25vw',
            }}
          >
            <LinkButton
              fullWidth
              color="purple"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<EmtIcon className="emt-message-text-alt-1" />}
              href={continueHref}
              disabled={petsittersIds.length === 0}
              sx={{
                height: isMobile ? 'unset' : '60px',
                flexGrow: 1,
                minHeight: 0,
                ...(petsittersIds.length === 0 && {
                  borderColor: 'greyGradient.150',
                }),
              }}
            >
              Continuer
            </LinkButton>
          </Box>
          {/* END Right */}
        </EmtPaper>
      </Box>
    )
  },
)

PetsittersSelection.displayName = 'PetsittersSelection'

export default PetsittersSelection
